/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { InstagramReel, YouTubeVideo, Tweet } from '../types';
import { SOCIAL_API_CONFIG, SOCIAL_LINKS, socialIntegrations } from '../config';
import { instagramReels, youTubeVideos, tweets } from '../data/testimonialsData';
import { Logger } from './logger';
import { apiClient, isExternalBackend } from './apiClient';

const IG_GRAPH_API = 'https://graph.instagram.com';
const YT_API_BASE = 'https://www.googleapis.com/youtube/v3';
const TW_API_BASE = 'https://api.twitter.com/2';

/**
 * Fetches Instagram media using the Basic Display API.
 * When external backend is enabled, proxies through backend.
 */
export const fetchInstagramReels = async (): Promise<InstagramReel[]> => {
    if (isExternalBackend()) {
        try {
            return await apiClient.get<InstagramReel[]>('/api/v1/social/instagram/reels');
        } catch (error) {
            Logger.error('Failed to fetch Instagram reels from backend', error);
            return [];
        }
    }

    const accessToken = SOCIAL_API_CONFIG.INSTAGRAM.ACCESS_TOKEN;

    if (!accessToken || accessToken.includes('PLACEHOLDER')) {
        Logger.warn(`[MOCK MODE] Instagram Access Token is missing. Using mock data.`);
        return instagramReels.map((reel: any, idx: number) => ({
            ...reel,
            id: `mock-ig-${idx}`,
            caption: reel.captionKey || 'Mock Instagram Caption',
            timestamp: new Date().toISOString()
        }));
    }

    try {
        const response = await fetch(`${IG_GRAPH_API}/me/media?fields=id,caption,media_type,media_url,permalink,thumbnail_url,timestamp&access_token=${accessToken}`);
        if (!response.ok) throw new Error('Instagram API request failed');

        const data = await response.json();
        return data.data.map((item: any) => ({
            id: item.id,
            thumbnailUrl: item.thumbnail_url || item.media_url,
            videoUrl: item.permalink,
            author: 'Constantina',
            avatarUrl: 'https://raw.githubusercontent.com/ammaarreshi/app-images/main/constantina-avatar.jpg',
            caption: item.caption || '',
            timestamp: item.timestamp
        }));
    } catch (error) {
        Logger.error('Error fetching Instagram Reels', error);
        return [];
    }
};

/**
 * Fetches YouTube videos. Prepared for real API.
 * When external backend is enabled, proxies through backend.
 */
export const fetchYouTubePlaylist = async (): Promise<YouTubeVideo[]> => {
    if (isExternalBackend()) {
        try {
            return await apiClient.get<YouTubeVideo[]>('/api/v1/social/youtube/videos');
        } catch (error) {
            Logger.error('Failed to fetch YouTube videos from backend', error);
            return [];
        }
    }

    const { API_KEY: apiKey, CHANNEL_ID: playlistId } = SOCIAL_API_CONFIG.YOUTUBE;

    if (!apiKey || apiKey.includes('PLACEHOLDER')) {
        Logger.warn(`[MOCK MODE] YouTube API Key is missing. Using mock data.`);
        return youTubeVideos.map((video: any) => ({
            ...video,
            title: video.titleKey || 'Mock YouTube Video',
            publishedAt: new Date().toISOString()
        }));
    }

    try {
        const response = await fetch(`${YT_API_BASE}/playlistItems?part=snippet&playlistId=${playlistId}&key=${apiKey}`);
        if (!response.ok) throw new Error('YouTube API request failed');

        const data = await response.json();
        if (!data.items || data.items.length === 0) {
            Logger.warn('YouTube API returned no items. Using mock data.');
            return youTubeVideos.map((video: any) => ({
                ...video,
                title: video.titleKey ? video.titleKey : video.title,
                publishedAt: new Date().toISOString()
            }));
        }

        return data.items.map((item: any) => ({
            id: item.snippet.resourceId.videoId,
            videoId: item.snippet.resourceId.videoId,
            thumbnailUrl: item.snippet.thumbnails.maxres?.url || item.snippet.thumbnails.high?.url,
            title: item.snippet.title,
            publishedAt: item.snippet.publishedAt
        }));
    } catch (error) {
        Logger.error('Error fetching YouTube Playlist', error);
        return youTubeVideos.map((video: any) => ({
            ...video,
            title: video.titleKey ? video.titleKey : video.title,
            publishedAt: new Date().toISOString()
        }));
    }
};

/**
 * Fetches Twitter feed. Prepared for real API.
 * When external backend is enabled, proxies through backend.
 */
export const fetchTwitterFeed = async (): Promise<Tweet[]> => {
    if (isExternalBackend()) {
        try {
            return await apiClient.get<Tweet[]>('/api/v1/social/twitter/feed');
        } catch (error) {
            Logger.error('Failed to fetch Twitter feed from backend', error);
            return [];
        }
    }

    const { apiKey } = socialIntegrations.twitter;

    if (!apiKey || apiKey.includes('PLACEHOLDER')) {
        Logger.warn(`[MOCK MODE] Twitter API Key is missing. Using mock data.`);
        return tweets.map((tweet: any) => ({
            ...tweet,
            author: tweet.authorKey || 'Mock Author',
            text: tweet.textKey || 'Mock Tweet Text',
            timestamp: new Date().toISOString()
        }));
    }

    try {
        // Example for Twitter v2 API
        const response = await fetch(`${TW_API_BASE}/tweets/search/recent?query=from:constantina&tweet.fields=created_at`, {
            headers: { 'Authorization': `Bearer ${apiKey}` }
        });
        if (!response.ok) throw new Error('Twitter API request failed');

        const data = await response.json();
        return data.data.map((item: any) => ({
            id: item.id,
            author: 'Constantina',
            avatarUrl: 'https://raw.githubusercontent.com/ammaarreshi/app-images/main/constantina-avatar.jpg',
            text: item.text,
            timestamp: item.created_at
        }));
    } catch (error) {
        Logger.error('Error fetching Twitter feed', error);
        return [];
    }
};

/**
 * Connects the user to a social service.
 * When external backend is enabled, routes through backend.
 */
export const connectToService = async (service: 'instagram' | 'twitter' | 'youtube' | 'facebook'): Promise<boolean> => {
    if (isExternalBackend()) {
        try {
            const res = await apiClient.post<{ success: boolean }>(`/api/v1/social/connect/${service}`);
            return res.success;
        } catch (error) {
            Logger.error(`Failed to connect to ${service} via backend`, error);
            return false;
        }
    }

    Logger.info(`Validating credentials for ${service}...`);

    let configValid = false;
    switch (service) {
        case 'instagram':
            configValid = !!socialIntegrations.instagram.appId && !socialIntegrations.instagram.appId.includes('PLACEHOLDER');
            break;
        case 'twitter':
            configValid = !!socialIntegrations.twitter.apiKey && !socialIntegrations.twitter.apiKey.includes('PLACEHOLDER');
            break;
        case 'youtube':
            configValid = !!socialIntegrations.youtube.apiKey && !socialIntegrations.youtube.apiKey.includes('PLACEHOLDER');
            break;
        case 'facebook':
            configValid = !!socialIntegrations.facebook.appId && !socialIntegrations.facebook.appId.includes('PLACEHOLDER');
            break;
    }

    if (!configValid) {
        let missingKey = '';
        switch (service) {
            case 'instagram': missingKey = 'socialIntegrations.instagram.appId'; break;
            case 'twitter': missingKey = 'socialIntegrations.twitter.apiKey'; break;
            case 'youtube': missingKey = 'socialIntegrations.youtube.apiKey'; break;
            case 'facebook': missingKey = 'socialIntegrations.facebook.appId'; break;
        }
        Logger.warn(`[MOCK MODE] Configuration for ${service} is missing or contains placeholders. Missing key: ${missingKey}. Simulation will proceed but real integration would fail.`);
    }

    Logger.info(`Simulating connection to ${service}...`);
    // Simulate redirect and callback delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    return true;
};

/**
 * Disconnects the user from a social service.
 */
export const disconnectFromService = async (service: 'instagram' | 'twitter' | 'youtube' | 'facebook'): Promise<boolean> => {
    Logger.info(`Simulating disconnection from ${service}...`);
    await new Promise(resolve => setTimeout(resolve, 1000));
    return true;
};
